import type { VoidComponent } from "solid-js";
import { createMemo, Show } from "solid-js";
import ContentSection from "../ContentSection";
import { useTranslation } from "../../../state/i18n";
import Wysiwyg from "../../../components/wysiwyg/Wysiwyg";
import type { Subform, SubformEmailConfiguration } from "../../../state/formVersion";
import { Checkbox } from "../../../components/checkbox/Checkbox";
import { Toggle } from "../../../components/toggle/Toggle";
import SubformService from "../../../services/SubformService";
import { FieldsAccordion } from "../../../components/accordion/Accordion";
import type { PinFieldProperties } from "../../../fields/media/PinField";
import CheckboxFieldListItem from "../../../components/listItem/CheckboxFieldListItem";
import { useFormServices } from "../../../services/formServices";
import { IconButton } from "../../../components/button/Button";
import { HtmlInputWithPreview } from "../../../components/inputs/HtmlInput";
import { WarningModal } from "../../../components/modal/Modal";
import ApiService from "../../../services/ApiService";
import { isEmpty } from "lodash-es";
import { metaVariableOption, toFieldValueOptions } from "../../../utils/selectOptionsUtil";
import type { FieldType } from "../../../types";
import FeatureService from "../../../services/FeatureService";
import Text from "../../../components/text/Text";
import { parseStringTemplate } from "../../../utils/stringTemplateUtil";
import type { VariableType } from "../../../variables/types";
import { toVariableValueOption } from "../../../variables/variableUtil";
import EmptyState from "../../../components/emptyState/EmptyState";
import { createDynamicValueOptions } from "../../../utils/menu/searchableMenuUtil";
import SeparatorLine from "../../../components/SeparatorLine";
import { useModalManager } from "../../../components/modal/ModalManager";
import { IconTile } from "../../../components/icon/IconTile";

const ALLOWED_PLACEHOLDER_FIELDS: FieldType[] = [
  "address",
  "barcode",
  "calculation",
  "catalogue",
  "checkbox",
  "currentLocation",
  "date",
  "dateTime",
  "email",
  "file",
  "iban",
  "licensePlate",
  "location",
  "lookup",
  "number",
  "phone",
  "photo",
  "price",
  "radio",
  "rating",
  "readOnlyText",
  "search",
  "signature",
  "slider",
  "smiley",
  "stopwatch",
  "subform",
  "text",
  "textArea",
  "time",
  "timeDifference",
  "video",
];

const ALLOWED_PLACEHOLDER_VARIABLES: VariableType[] = ["string", "number", "boolean", "date", "datetime"];

type SubformEditorEmailContentProps = {
  subform: Subform;
};

const SubformEditorEmailContent: VoidComponent<SubformEditorEmailContentProps> = (props) => {
  const { t } = useTranslation();
  const { open } = useModalManager();
  const { fieldService, variableService } = useFormServices();

  const resetPdfLayout = async (emailConfiguration: SubformEmailConfiguration) => {
    const defaultContent = await ApiService.generateDefaultPdfContent(
      fieldService.getFields(),
      variableService.getVariables(),
      {
        hideNoValues: emailConfiguration.hideNoValues,
        horizontalOrientation: emailConfiguration.horizontalOrientation,
      },
    );
    SubformService.updateEmailConfigurationProperty(props.subform.uid, "pdfContent", defaultContent);
  };

  const toggleCustomPdf = async (enabled: boolean, emailConfiguration: SubformEmailConfiguration) => {
    if (enabled && isEmpty(emailConfiguration.pdfContent)) {
      await resetPdfLayout(emailConfiguration);
    }
    SubformService.updateEmailConfigurationProperty(props.subform.uid, "simpleMode", !enabled);
  };

  const placeholderMenuOptions = createMemo(() =>
    createDynamicValueOptions(t, {
      fields: [],
      subforms: [
        {
          [t(($) => $.MENU_SECTION_SUBFORMS_CURRENT)]: fieldService
            .getFieldsByTypes(ALLOWED_PLACEHOLDER_FIELDS)
            .flatMap((field) => toFieldValueOptions(field)),
        },
      ],
      customVariables: variableService.getVariablesByTypes(ALLOWED_PLACEHOLDER_VARIABLES).map(toVariableValueOption),
      metaVariables: [
        metaVariableOption("info.userId"),
        metaVariableOption("meta.location.longitude"),
        metaVariableOption("meta.location.latitude"),
        metaVariableOption("meta.registrationDate"),
        metaVariableOption("meta.guid"),
        metaVariableOption("meta.taskId"),
        metaVariableOption("meta.serialNumber"),
        metaVariableOption("info.formName"),
      ],
    }),
  );

  return (
    <Show
      when={props.subform.emailConfiguration}
      fallback={
        <EmptyState
          illustration={{ primaryIcon: "mail" }}
          title={t(($) => $.SUBFORMS_EMAIL_CONFIGURATION_EMPTY_STATE_TITLE)}
          description={t(($) => $.SUBFORMS_EMAIL_CONFIGURATION_EMPTY_STATE_DESCRIPTION)}
          action={{
            variant: "light",
            text: t(($) => $.SUBFORMS_EMAIL_CONFIGURATION_CREATE_BUTTON),
            icon: "plus",
            onClick: () => SubformService.addEmailConfiguration(props.subform.uid),
          }}
        />
      }
    >
      {(emailConfiguration) => (
        <ContentSection>
          <div role="group" class="flex flex-col gap-6">
            <div class="flex items-center gap-2">
              <IconTile icon="mail" size="md" border />

              <div class="flex grow flex-col overflow-hidden pr-16">
                <Text
                  class="text-base-medium text-primary"
                  text={t(($) => $.SUBFORMS_EMAIL_CONFIGURATION_TITLE)}
                  truncate
                />
                <Text
                  as="h3"
                  class="text-medium-semibold text-primary"
                  text={t(($) => $.SUBFORMS_EMAIL_CONFIGURATION_TITLE)}
                  truncate
                />
              </div>

              <IconButton
                variant="tertiary"
                size="medium"
                icon="trash"
                aria-label={t(($) => $.DELETE_ARIA)}
                onClick={() => SubformService.deleteEmailConfiguration(props.subform.uid)}
              />
            </div>

            <Wysiwyg
              content={emailConfiguration().content}
              onChange={(value) => SubformService.updateEmailConfigurationProperty(props.subform.uid, "content", value)}
              placeholderMenuOptions={placeholderMenuOptions()}
              ariaLabel={t(($) => $.SUBFORMS_EMAIL_CONFIGURATION_EMAIL_CONTENT)}
              parse={(value) =>
                parseStringTemplate(
                  value,
                  fieldService.getFieldsByTypes(ALLOWED_PLACEHOLDER_FIELDS),
                  variableService.getVariablesByTypes(ALLOWED_PLACEHOLDER_VARIABLES),
                )
              }
            />

            <FieldsAccordion
              items={[
                {
                  label: t(($) => $.SUBFORMS_EMAIL_CONFIGURATION_PIN_FIELDS_TO_ATTACH_LABEL),
                  key: "pinFields",
                  components: fieldService.getFieldsByType<PinFieldProperties>("pin").map((field) => {
                    const pinFieldsToAttach = emailConfiguration().pinFieldsToAttach;

                    return {
                      Component: () => (
                        <CheckboxFieldListItem
                          checked={pinFieldsToAttach.includes(field.uid)}
                          setChecked={(value) => {
                            const newValue = value
                              ? [...pinFieldsToAttach, field.uid]
                              : pinFieldsToAttach.filter((fieldId) => !fieldId.equals(field.uid));

                            SubformService.updateEmailConfigurationProperty(
                              props.subform.uid,
                              "pinFieldsToAttach",
                              newValue,
                            );
                          }}
                          title={`${field.properties.data_name}.pdf`}
                          category="media"
                          icon="pin"
                          dataName={field.properties.data_name}
                        />
                      ),
                      filterKey: "",
                    };
                  }),
                },
              ]}
            />

            <div>
              <h2 class="text-header-xl text-primary">{t(($) => $.SUBFORMS_EMAIL_CONFIGURATION_SECTION_PDF_TITLE)}</h2>
              <SeparatorLine class="mt-2.5" />
            </div>

            <Checkbox
              label={t(($) => $.SUBFORMS_EMAIL_CONFIGURATION_HIDE_NO_VALUES)}
              block
              checked={emailConfiguration().hideNoValues}
              setChecked={(value) =>
                SubformService.updateEmailConfigurationProperty(props.subform.uid, "hideNoValues", value)
              }
              info={{
                title: t(($) => $.SUBFORMS_EMAIL_CONFIGURATION_HIDE_NO_VALUES),
                alt: t(($) => $.SUBFORMS_EMAIL_CONFIGURATION_PROPERTY_DESCRIPTION_ARIA_LABEL, {
                  property: t(($) => $.SUBFORMS_EMAIL_CONFIGURATION_HIDE_NO_VALUES),
                }),
                description: t(($) => $.SUBFORMS_EMAIL_CONFIGURATION_HIDE_NO_VALUES_DESCRIPTION),
              }}
            />

            <Checkbox
              label={t(($) => $.SUBFORMS_EMAIL_CONFIGURATION_HORIZONTAL_ORIENTATION)}
              block
              checked={emailConfiguration().horizontalOrientation}
              setChecked={(value) =>
                SubformService.updateEmailConfigurationProperty(props.subform.uid, "horizontalOrientation", value)
              }
              info={{
                title: t(($) => $.SUBFORMS_EMAIL_CONFIGURATION_HORIZONTAL_ORIENTATION),
                alt: t(($) => $.SUBFORMS_EMAIL_CONFIGURATION_PROPERTY_DESCRIPTION_ARIA_LABEL, {
                  property: t(($) => $.SUBFORMS_EMAIL_CONFIGURATION_HORIZONTAL_ORIENTATION),
                }),
                description: t(($) => $.SUBFORMS_EMAIL_CONFIGURATION_HORIZONTAL_ORIENTATION_DESCRIPTION),
              }}
            />

            <Show when={FeatureService.hasFeature("HOOKS")}>
              <div class="flex flex-col gap-4">
                <Toggle
                  label={t(($) => $.SUBFORMS_EMAIL_CONFIGURATION_SIMPLE_MODE_LABEL)}
                  block
                  checked={!emailConfiguration().simpleMode}
                  setChecked={(checked) => toggleCustomPdf(checked, emailConfiguration())}
                  info={{
                    title: t(($) => $.SUBFORMS_EMAIL_CONFIGURATION_SIMPLE_MODE_LABEL),
                    alt: t(($) => $.SUBFORMS_EMAIL_CONFIGURATION_PROPERTY_DESCRIPTION_ARIA_LABEL, {
                      property: t(($) => $.SUBFORMS_EMAIL_CONFIGURATION_SIMPLE_MODE_LABEL),
                    }),
                    description: t(($) => $.SUBFORMS_EMAIL_CONFIGURATION_SIMPLE_MODE_DESCRIPTION),
                    href: t(($) => $.SUBFORMS_EMAIL_CONFIGURATION_SIMPLE_MODE_INFO_LINK),
                  }}
                />

                <Show when={!emailConfiguration().simpleMode}>
                  <HtmlInputWithPreview
                    label=""
                    ariaLabel={t(($) => $.SUBFORMS_EMAIL_CONFIGURATION_PDF_CONTENT_LABEL)}
                    message={{
                      label: t(($) => $.SUBFORMS_EMAIL_CONFIGURATION_PDF_CONTENT_EDITOR_INFORMATIVE_MESSAGE),
                      action: {
                        label: t(($) => $.SUBFORMS_EMAIL_CONFIGURATION_PDF_CONTENT_RESET_LAYOUT_ACTION_LABEL),
                        onClick: () =>
                          open({
                            content: () => (
                              <WarningModal
                                confirmLabel={t(($) => $.CONTINUE_BUTTON)}
                                cancelLabel={t(($) => $.CANCEL_BUTTON)}
                                title={t(($) => $.SUBFORMS_EMAIL_CONFIGURATION_PDF_CONTENT_RESET_LAYOUT_MODAL_TITLE)}
                                description={t(
                                  ($) => $.SUBFORMS_EMAIL_CONFIGURATION_PDF_CONTENT_RESET_LAYOUT_MODAL_DESCRIPTION,
                                )}
                                onConfirm={() => resetPdfLayout(emailConfiguration())}
                              />
                            ),
                          }),
                      },
                    }}
                    value={emailConfiguration().pdfContent}
                    onChange={(value) =>
                      SubformService.updateEmailConfigurationProperty(props.subform.uid, "pdfContent", value)
                    }
                  />
                </Show>
              </div>
            </Show>
          </div>
        </ContentSection>
      )}
    </Show>
  );
};

export default SubformEditorEmailContent;
